import { useCallback, useState } from "react";

import { type FilterDraft } from "~/shared/lib/filter-utils";
import { useSectionPageSize } from "~/shared/lib/use-grid-columns";
import { useIsMobile } from "~/shared/lib/use-media-query";
import { type SectionView } from "./SectionViewSwitcher";

/** Минимальный контракт `useTableSettings`, нужный пагинации секции. */
interface PageSizeTarget {
    pageSize: number;
    setPageSize: (size: number) => void;
}

/** Минимальный контракт `useSectionState`, нужный пагинации секции. */
interface PageTarget {
    setPage: (page: number) => void;
}

/**
 * Effective fetch size for a section list: on mobile the view is always card, and the
 * target page size is rounded up to a full row of the current responsive column count,
 * so the last row is never half-empty. The selector keeps showing the target.
 */
export function useEffectivePageSize(view: SectionView, targetPageSize: number): number {
    const isMobile = useIsMobile();
    return useSectionPageSize(isMobile ? "card" : view, targetPageSize);
}

/**
 * Общий обработчик пагинации секции: смена размера страницы возвращает на первую,
 * смена страницы переводит 0-based индекс таблицы в 1-based номер секции.
 */
export function useSectionPagination(
    section: PageTarget,
    tableSettings: PageSizeTarget,
): (pageIndex: number, pageSize: number) => void {
    return useCallback(
        (newPageIndex: number, newPageSize: number) => {
            if (newPageSize !== tableSettings.pageSize) {
                tableSettings.setPageSize(newPageSize);
                section.setPage(1);
            } else {
                section.setPage(newPageIndex + 1);
            }
        },
        [tableSettings, section],
    );
}

/** Минимальный контракт `useSectionState`, нужный черновику фильтров. */
interface FiltersTarget<TFilters extends Record<string, readonly string[]>> {
    setFilters: (entries: Partial<Record<keyof TFilters & string, readonly string[]>>) => void;
    resetFilters: () => void;
}

interface UseFilterDraftOptions<TFilters extends Record<string, readonly string[]>> {
    section: FiltersTarget<TFilters>;
    /** Начальный черновик — обычно `<slice>DraftFromFilters(section.filters)`. */
    initialDraft: () => FilterDraft;
    /** Перенос черновика в URL-фильтры — `apply<Slice>Draft` соответствующего слайса. */
    applyDraft: (draft: FilterDraft, setFilters: FiltersTarget<TFilters>["setFilters"]) => void;
    /**
     * Зависимые поля: смена ключа обнуляет перечисленные. Пример — `{ project: ["scene"] }`:
     * сцены живут внутри проекта, и сцена, выбранная под прежним проектом, невалидна.
     */
    dependentFields?: Readonly<Record<string, readonly string[]>>;
}

interface FilterDraftState {
    filterDraft: FilterDraft;
    onFilterChange: (key: string, value: unknown) => void;
    handleFilterApply: () => void;
    handleFilterReset: () => void;
}

/**
 * Черновик панели фильтров секции: правки копятся локально и уезжают в URL одним
 * применением. `setFilters` / `resetFilters` внутри `useSectionState` сами сбрасывают
 * страницу на первую (одна запись в URL) — отдельный `setPage(1)` здесь добавлять нельзя:
 * два `setSearchParams` в одном тике затирают друг друга.
 */
export function useFilterDraft<TFilters extends Record<string, readonly string[]>>({
    section,
    initialDraft,
    applyDraft,
    dependentFields,
}: UseFilterDraftOptions<TFilters>): FilterDraftState {
    const [filterDraft, setFilterDraft] = useState<FilterDraft>(initialDraft);

    const onFilterChange = useCallback(
        (key: string, value: unknown) => {
            setFilterDraft((prev) => {
                const next = { ...prev, [key]: value };
                for (const dependent of dependentFields?.[key] ?? []) next[dependent] = undefined;
                return next;
            });
        },
        [dependentFields],
    );

    const handleFilterApply = useCallback(() => {
        applyDraft(filterDraft, section.setFilters);
    }, [applyDraft, filterDraft, section]);

    const handleFilterReset = useCallback(() => {
        setFilterDraft({});
        section.resetFilters();
    }, [section]);

    return { filterDraft, onFilterChange, handleFilterApply, handleFilterReset };
}
